import React, { useContext, useMemo } from 'react';

import Loading from 'components/Loading';
import Typography from 'kingpin/atoms/Typography';
import WarningBanner from 'components/WarningBanner';
import CompoundMetricsContext from 'contexts/CompoundMetricsContext';
import useMetric from 'hooks/useMetric';
import useBonusMetricAssignments from 'components/PerformanceBoards/hooks/useBonusMetricAssignments';
import useSlideoutRollingDateScope from 'components/PerformanceBoards/hooks/useSlideoutRollingDateScope';
import getMetricFiltersByDataset from 'components/PerformanceBoards/hooks/getMetricFiltersByDataset';

import MetricDocumentGroup from './MetricDocumentGroup';

// Stable reference so a dataset with no metric filters doesn't hand
// MetricDocumentGroup a new array each render (which would refire its fetch).
const EMPTY_METRIC_FILTERS: FilterInput[] = [];

const RelatedMetricDocumentsContainer = ({
  section,
}: {
  section: PerformanceBoardTypes.SlideOut.RelatedMetricDocumentsSection;
}) => {
  const { byFieldName, isLoading } = useBonusMetricAssignments();
  const assignment = byFieldName[section.bonusMetricFieldName];
  const metric = useMetric(assignment?.metricId);
  const { compoundMetricInputsLookup } = useContext(CompoundMetricsContext);

  const filtersByDataset = useMemo(
    () => getMetricFiltersByDataset({ metric, compoundMetricInputsLookup }),
    [metric, compoundMetricInputsLookup],
  );

  const rolling: PerformanceBoardTypes.SlideOut.RollingDateFilter | undefined =
    useMemo(
      () =>
        assignment && assignment.datePeriodType === 'to_date'
          ? {
              mode: 'Rolling',
              lookback: {
                type: 'last',
                interval: 'week',
                n: assignment.datePeriodLength,
              },
              dateField: assignment.dateField,
            }
          : undefined,
      [assignment],
    );

  const dateScope = useSlideoutRollingDateScope(rolling);

  if (isLoading) {
    return <Loading />;
  }

  if (!assignment || assignment.datePeriodType !== 'to_date') {
    return (
      <WarningBanner isShort>
        <Typography.Body type="Body 12">
          This widget&apos;s bonus metric is unavailable or its date period is
          not &quot;To Date&quot;. Update it in Manage Bonus Metrics.
        </Typography.Body>
      </WarningBanner>
    );
  }

  const isMultiple = section.datasets.length > 1;

  return (
    <div>
      {section.datasets.map((group) => (
        <MetricDocumentGroup
          key={group.dataType}
          dataType={group.dataType}
          heading={group.heading}
          showHeading={isMultiple}
          fields={group.fields}
          dateScope={dateScope}
          metricFilters={
            filtersByDataset[group.dataType] ?? EMPTY_METRIC_FILTERS
          }
        />
      ))}
    </div>
  );
};

export default RelatedMetricDocumentsContainer;
